/**
 * StepGit — Etapa 2: Repositório Git.
 * F1-FE-2: UI de conexão OAuth Git, seleção repo/branch,
 *          botão de indexação e barra de progresso real.
 *
 * Fluxo:
 *   1. Mostrar providers disponíveis (GET /auth/git/providers)
 *   2. Botão "Conectar" → window.location.href para OAuth
 *   3. Providers conectados: listar repos cadastrados (GET .../git-repositories)
 *   4. Adicionar repo/branch → POST .../git-repositories
 *   5. Indexar → POST .../git-repositories/{id}/index (202)
 *   6. Barra de progresso real via polling GET .../index/status (refetchInterval 2s)
 *
 * Paleta C, estilos inline, pt-BR, a11y.
 */
import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Github, GitBranch, Plus, Trash2, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { MutationError, QueryError, LoadingSpinner } from '@/components/ui/QueryState';
import { ForbiddenBanner } from '@/components/ui/ForbiddenBanner';
import { RateLimitBanner } from '@/components/ui/RateLimitBanner';
import {
  useGitProviders,
  useGitRepositories,
  useGitHubRepositories,
  useGitHubBranches,
  useCreateGitHubRepository,
  useGitHubOrgs,
  useAddGitRepository,
  useRemoveGitRepository,
  useStartIndexing,
  useIndexingStatus,
  usePublicIndex,
  useUpdateRepoRole,
  useGraphStatus,
  gitKeys,
} from '@/api/git';
import { rotuloDoGrafo, avisoDeBranch, avisoDeEntrega } from './grafoDoRepo';
import { useJourney } from '../JourneyContext';

// ─── Constantes ───────────────────────────────────────────────────────────────

// Branches comuns oferecidas como fallback além da default_branch do repo real.
const BRANCH_OPTIONS = [
  'main',
  'master',
  'develop',
  'release/2026.2',
  'feature/customers-v2',
  'hotfix/lgpd',
];

// Papéis do repositório no roteamento multi-repo do codegen. Auto-classificado
// no backend; o usuário confirma/edita aqui.
const ROLE_OPTIONS = [
  { value: 'frontend', label: 'Frontend' },
  { value: 'backend',  label: 'Backend' },
  { value: 'database', label: 'Banco de dados' },
  { value: 'infra',    label: 'Infra' },
  { value: 'unknown',  label: 'Indefinido' },
];

// ─── RepoRoleSelect ────────────────────────────────────────────────────────────

// Seletor compacto do papel do repositório. Faz PATCH .../role no change e
// invalida a lista (via mutation) para refletir o papel confirmado.
function RepoRoleSelect({ projectId, repoId, role }) {
  const updateRole = useUpdateRepoRole(projectId);
  const current = ROLE_OPTIONS.some((o) => o.value === role) ? role : 'unknown';

  return (
    <div className="flex items-center gap-1.5">
      {updateRole.error && (
        <MutationError error={updateRole.error} onDismiss={() => updateRole.reset()} />
      )}
      <label htmlFor={`repo-role-${repoId}`} style={{ fontSize: 11.5, color: C.neutralMuted }}>
        Papel:
      </label>
      <select
        id={`repo-role-${repoId}`}
        value={current}
        onChange={(e) => updateRole.mutate({ repoId, role: e.target.value })}
        disabled={updateRole.isPending}
        aria-label="Papel do repositório"
        aria-busy={updateRole.isPending}
        style={{
          padding: '4px 8px',
          backgroundColor: C.black,
          border: `1px solid ${C.blackBorder}`,
          borderRadius: 6,
          color: C.neutral,
          fontSize: 12,
        }}
      >
        {ROLE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── GrafoDoRepo ───────────────────────────────────────────────────────────────

// Estado do GRAFO de código do repositório + aviso de branch. O grafo deixou de ser
// opcional no brownfield: sem ele (ou com ele falho) a execução é recusada, então a
// tela diz o estado e o motivo em vez de mostrar só "Indexado".
function GrafoDoRepo({ grafo }) {
  const rotulo = rotuloDoGrafo(grafo);
  const aviso = avisoDeBranch(grafo) ?? avisoDeEntrega(grafo);
  if (!rotulo && !aviso) return null;
  const cor = { ok: C.cyber, erro: C.hot, neutro: C.neutralMuted }[rotulo?.tom] ?? C.neutralMuted;
  return (
    <div style={{ fontSize: 12, marginTop: 2 }}>
      {rotulo && (
        <span style={{ color: cor }} role={rotulo.tom === 'erro' ? 'alert' : undefined}>
          {rotulo.texto}
        </span>
      )}
      {aviso && (
        <div style={{ color: C.warn ?? C.hot, marginTop: 2 }}>
          Atenção: {aviso}.
        </div>
      )}
    </div>
  );
}

// ─── IndexingProgressBar ───────────────────────────────────────────────────────

function IndexingProgressBar({ projectId, repoId, repoName }) {
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const startIndexing = useStartIndexing(projectId);

  const statusQuery = useIndexingStatus(projectId, repoId, pollingEnabled);
  const status = statusQuery.data;

  const handleStartIndex = useCallback(async () => {
    try {
      await startIndexing.mutateAsync(repoId);
      setPollingEnabled(true);
    } catch { /* tratado pelo MutationError */ }
  }, [startIndexing, repoId]);

  // O backend (IndexStatusOut / Temporal query) usa: status running|done|failed|
  // cancelled, progress_pct, indexed_docs, total_docs.
  const isIndexing  = status?.status === 'running' || status?.status === 'pending';
  const isCompleted = status?.status === 'done';
  const isFailed    = status?.status === 'failed' || status?.status === 'cancelled';
  const progress    = Math.round(status?.progress_pct ?? 0);
  const indexedDocs = status?.indexed_docs;
  const totalDocs   = status?.total_docs;

  // Fim da indexação (deu certo ou não): o estado persistido mudou — índice, branch
  // (o worker corrige o cadastro quando usa a default) e GRAFO. Sem isto a linha do
  // repositório seguia mostrando o estado de antes até recarregar a página.
  const queryClient = useQueryClient();
  const terminou = isCompleted || isFailed;
  useEffect(() => {
    if (!terminou || !projectId) return;
    queryClient.invalidateQueries({ queryKey: gitKeys.repos(projectId) });
    queryClient.invalidateQueries({ queryKey: gitKeys.graphStatus(projectId) });
  }, [terminou, projectId, queryClient]);

  return (
    <div style={{ marginTop: 12 }}>
      {/* Erro de start indexing */}
      {startIndexing.error && (
        <MutationError error={startIndexing.error} onDismiss={() => startIndexing.reset()} />
      )}

      {!pollingEnabled && !isCompleted && !isFailed && (
        <Btn
          variant="outline"
          size="sm"
          icon={RefreshCw}
          onClick={handleStartIndex}
          disabled={startIndexing.isPending}
          aria-busy={startIndexing.isPending}
        >
          {startIndexing.isPending ? 'Iniciando...' : 'Indexar repositório'}
        </Btn>
      )}

      {/* Barra de progresso durante indexação */}
      {(isIndexing || pollingEnabled) && !isCompleted && !isFailed && (
        <div style={{ marginTop: 8 }}>
          <div className="flex items-center justify-between mb-2">
            <span style={{ fontSize: 12, color: C.neutralDim }}>
              Indexando {repoName}...
            </span>
            <span style={{ fontSize: 12, color: C.cyber, fontWeight: 600 }}>
              {progress}%
            </span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Indexação ${repoName}: ${progress}%`}
            style={{
              height: 4,
              backgroundColor: C.black,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                backgroundColor: C.cyber,
                borderRadius: 2,
                transition: 'width 500ms ease',
              }}
            />
          </div>
          {indexedDocs != null && (
            <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 4 }}>
              {indexedDocs} / {totalDocs ?? '?'} arquivos
            </div>
          )}
        </div>
      )}

      {/* Concluído */}
      {isCompleted && (
        <div className="flex items-center gap-2 mt-2">
          <CheckCircle2 size={14} style={{ color: C.cyber }} aria-hidden="true" />
          <span style={{ fontSize: 12, color: C.cyber, fontWeight: 600 }}>
            Indexado com sucesso
            {indexedDocs != null && ` · ${indexedDocs} arquivos`}
          </span>
        </div>
      )}

      {/* Falhou */}
      {isFailed && (
        <div className="flex items-center gap-2 mt-2">
          <AlertCircle size={14} style={{ color: C.hot }} aria-hidden="true" />
          <span style={{ fontSize: 12, color: C.hot }}>
            Falha na indexação{status?.error ? `: ${status.error}` : ''}.{' '}
          </span>
          <button
            onClick={() => { setPollingEnabled(false); startIndexing.reset(); }}
            style={{ fontSize: 12, color: C.cyber, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Tentar novamente
          </button>
        </div>
      )}
    </div>
  );
}

// ─── ProviderCard ─────────────────────────────────────────────────────────────

function ProviderCard({ provider, connected, username, authMethod, onConnect }) {
  const Icon = provider === 'github' ? Github : GitBranch;
  const label = provider === 'github' ? 'GitHub' : 'Bitbucket';
  const color = connected ? C.cyber : C.neutralMuted;
  const connectedLabel = username
    ? `@${username}`
    : authMethod === 'token'
      ? 'Conectado via token (GITHUB_WRITE_TOKEN)'
      : 'Conectado';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        borderRadius: 8,
        backgroundColor: connected ? `${C.cyber}08` : C.blackElev2,
        border: `1px solid ${connected ? C.cyberDim : C.blackBorder}`,
      }}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} style={{ color }} aria-hidden="true" />
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: C.neutral }}>{label}</div>
          {connected && (
            <div style={{ fontSize: 12, color: C.neutralDim }}>{connectedLabel}</div>
          )}
          {!connected && (
            <div style={{ fontSize: 12, color: C.neutralMuted }}>Não conectado</div>
          )}
        </div>
      </div>
      {connected ? (
        <Badge color={C.cyber} light>CONECTADO</Badge>
      ) : (
        <Btn variant="outline" size="sm" onClick={() => onConnect(provider)}>
          Conectar
        </Btn>
      )}
    </div>
  );
}

// ─── AddRepoForm ──────────────────────────────────────────────────────────────

function AddRepoForm({ projectId, connectedProviders = [], onAdded }) {
  const [repo,   setRepo]   = useState('');
  const [branch, setBranch] = useState('main');
  const addRepo = useAddGitRepository(projectId);
  const createRepo = useCreateGitHubRepository();

  // Provedor ativo (GitHub/Bitbucket). Default: o primeiro conectado.
  const [provider, setProvider] = useState(connectedProviders[0] ?? 'github');
  const providerConnected = connectedProviders.includes(provider);
  const cloneHost = provider === 'bitbucket' ? 'bitbucket.org' : 'github.com';

  // Se o provider ativo não estiver conectado mas houver algum conectado, adota-o
  // (ex.: só Bitbucket conectado, mas o default era 'github').
  const connectedKey = connectedProviders.join(',');
  useEffect(() => {
    if (connectedProviders.length && !connectedProviders.includes(provider)) {
      setProvider(connectedProviders[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedKey]);

  // Criação de repositório novo
  const [creating,       setCreating]       = useState(false);
  const [newRepoName,    setNewRepoName]    = useState('');
  const [newRepoPrivate, setNewRepoPrivate] = useState(true);
  const [newRepoDesc,    setNewRepoDesc]    = useState('');
  const [newRepoOwner,   setNewRepoOwner]   = useState(''); // '' = conta pessoal; senão org/workspace

  // Orgs (GitHub) / workspace (Bitbucket) — só busca ao abrir o painel de criação.
  const orgsQuery = useGitHubOrgs(providerConnected && creating, provider);
  const orgs      = Array.isArray(orgsQuery.data) ? orgsQuery.data : [];

  // Repos REAIS do provider (só busca se conectado).
  const ghReposQuery = useGitHubRepositories(providerConnected, provider);
  const ghRepos      = Array.isArray(ghReposQuery.data) ? ghReposQuery.data : [];
  const loadingRepos = providerConnected && ghReposQuery.isLoading;

  // Cai para entrada manual (owner/nome) se não conectado, se a listagem falhar,
  // ou se não houver repositórios.
  const useManual =
    !providerConnected ||
    Boolean(ghReposQuery.error) ||
    (!loadingRepos && ghRepos.length === 0);

  const selected = ghRepos.find((r) => r.full_name === repo);

  // Branches REAIS do repo selecionado (só busca quando há repo real escolhido).
  const branchesQuery = useGitHubBranches(repo, providerConnected && !useManual && Boolean(repo), provider);
  const realBranches  = Array.isArray(branchesQuery.data) ? branchesQuery.data : [];
  const loadingBranches = branchesQuery.isFetching && providerConnected && !useManual && Boolean(repo);

  // Opções de branch: reais (default_branch primeiro) quando disponíveis;
  // caso contrário, a default_branch do repo + comuns como fallback.
  const branchOptions = realBranches.length > 0
    ? Array.from(new Set([
        ...(selected?.default_branch ? [selected.default_branch] : []),
        ...realBranches,
      ]))
    : Array.from(new Set([
        ...(selected?.default_branch ? [selected.default_branch] : []),
        ...BRANCH_OPTIONS,
      ]));

  function handleRepoSelect(e) {
    const val = e.target.value;
    setRepo(val);
    const r = ghRepos.find((x) => x.full_name === val);
    if (r?.default_branch) setBranch(r.default_branch);
  }

  async function handleCreateRepo() {
    const name = newRepoName.trim();
    if (!name) return;
    try {
      const created = await createRepo.mutateAsync({
        provider,
        name,
        private: newRepoPrivate,
        description: newRepoDesc.trim(),
        ...(newRepoOwner ? { org: newRepoOwner } : {}),
      });
      // Auto-seleciona o repo recém-criado para o usuário só clicar "Adicionar".
      if (created?.full_name) {
        setRepo(created.full_name);
        setBranch(created.default_branch || 'main');
      }
      setCreating(false);
      setNewRepoName('');
      setNewRepoDesc('');
      ghReposQuery.refetch?.();
    } catch { /* tratado pelo MutationError */ }
  }

  async function handleAdd(e) {
    e.preventDefault();
    const full = repo.trim();
    if (!full) return;
    try {
      // Contrato do backend: { provider, full_name, branch, clone_url }.
      await addRepo.mutateAsync({
        provider,
        full_name: full,
        branch,
        clone_url: `https://${cloneHost}/${full}.git`,
      });
      setRepo('');
      setBranch('main');
      onAdded?.();
    } catch { /* tratado pelo MutationError */ }
  }

  return (
    <form onSubmit={handleAdd} style={{ marginTop: 16 }}>
      <MutationError error={addRepo.error} onDismiss={() => addRepo.reset()} />

      {/* Seletor de provedor (quando há mais de um conectado) */}
      {connectedProviders.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="repo-provider" style={{ fontSize: 12, color: C.neutralDim, marginRight: 8 }}>
            Provedor
          </label>
          <select
            id="repo-provider"
            value={provider}
            onChange={(e) => { setProvider(e.target.value); setRepo(''); setNewRepoOwner(''); }}
            style={{
              padding: '6px 10px', backgroundColor: C.blackElev2,
              border: `1px solid ${C.blackBorder}`, borderRadius: 6, color: C.neutral, fontSize: 13,
            }}
          >
            {connectedProviders.map((p) => (
              <option key={p} value={p}>{p === 'bitbucket' ? 'Bitbucket' : 'GitHub'}</option>
            ))}
          </select>
        </div>
      )}

      {/* Criar novo repositório no provedor configurado */}
      {providerConnected && !useManual && (
        creating ? (
          <div
            style={{
              marginBottom: 16, padding: 14, borderRadius: 8,
              backgroundColor: C.blackElev2, border: `1px solid ${C.cyberDim}`,
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.neutral, marginBottom: 10 }}>
              Novo repositório no GitHub
            </div>
            <MutationError error={createRepo.error} onDismiss={() => createRepo.reset()} />
            <div className="flex items-end gap-3 flex-wrap">
              {orgs.length > 0 ? (
                <div style={{ flex: '0 0 180px' }}>
                  <label htmlFor="new-repo-owner" style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
                    Owner
                  </label>
                  <select
                    id="new-repo-owner"
                    value={newRepoOwner}
                    onChange={(e) => setNewRepoOwner(e.target.value)}
                    style={{
                      width: '100%', padding: '9px 12px', backgroundColor: C.black,
                      border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                      color: C.neutral, fontSize: 13,
                    }}
                  >
                    <option value="">Conta pessoal</option>
                    {orgs.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              ) : (
                // Fallback: alguns tokens (fine-grained) não listam orgs em /user/orgs.
                // Permite digitar a org manualmente (ex.: Nava-Sandbox). Vazio = conta pessoal.
                <div style={{ flex: '0 0 200px' }}>
                  <label htmlFor="new-repo-owner-text" style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
                    Organização (opcional)
                  </label>
                  <input
                    id="new-repo-owner-text"
                    type="text"
                    value={newRepoOwner}
                    onChange={(e) => setNewRepoOwner(e.target.value.trim())}
                    placeholder="ex.: Nava-Sandbox"
                    title="Deixe vazio para criar na conta pessoal do token."
                    style={{
                      width: '100%', padding: '9px 12px', backgroundColor: C.black,
                      border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                      color: C.neutral, fontSize: 13, boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}
              <div style={{ flex: '1 1 200px' }}>
                <label htmlFor="new-repo-name" style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
                  Nome do repositório
                </label>
                <input
                  id="new-repo-name"
                  type="text"
                  value={newRepoName}
                  onChange={(e) => setNewRepoName(e.target.value)}
                  placeholder="meu-novo-projeto"
                  autoFocus
                  style={{
                    width: '100%', padding: '9px 12px', backgroundColor: C.black,
                    border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                    color: C.neutral, fontSize: 13, boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ flex: '2 1 240px' }}>
                <label htmlFor="new-repo-desc" style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
                  Descrição (opcional)
                </label>
                <input
                  id="new-repo-desc"
                  type="text"
                  value={newRepoDesc}
                  onChange={(e) => setNewRepoDesc(e.target.value)}
                  placeholder="Breve descrição"
                  style={{
                    width: '100%', padding: '9px 12px', backgroundColor: C.black,
                    border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                    color: C.neutral, fontSize: 13, boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
            <div className="flex items-center gap-3" style={{ marginTop: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: C.neutralDim, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newRepoPrivate}
                  onChange={(e) => setNewRepoPrivate(e.target.checked)}
                />
                Privado
              </label>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <Btn
                  type="button"
                  variant="primary"
                  size="sm"
                  icon={Plus}
                  onClick={handleCreateRepo}
                  disabled={!newRepoName.trim() || createRepo.isPending}
                  aria-busy={createRepo.isPending}
                >
                  {createRepo.isPending ? 'Criando...' : 'Criar repositório'}
                </Btn>
                <Btn type="button" variant="ghost" size="sm" onClick={() => setCreating(false)}>
                  Cancelar
                </Btn>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { createRepo.reset(); setCreating(true); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: C.cyber, fontSize: 12.5, fontWeight: 600, padding: 0,
            }}
          >
            <Plus size={13} aria-hidden="true" /> Criar novo repositório no {provider === 'bitbucket' ? 'Bitbucket' : 'GitHub'}
          </button>
        )
      )}

      <div className="flex items-end gap-3">
        <div style={{ flex: 1 }}>
          <label
            htmlFor="repo-select"
            style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}
          >
            Repositório{' '}
            {loadingRepos && (
              <span style={{ color: C.neutralMuted }}>· carregando do GitHub…</span>
            )}
            {!loadingRepos && !useManual && (
              <span style={{ color: C.cyber }}>· {ghRepos.length} do GitHub</span>
            )}
          </label>

          {useManual ? (
            <input
              id="repo-select"
              type="text"
              value={repo}
              onChange={(e) => setRepo(e.target.value)}
              placeholder="owner/nome-do-repo"
              aria-label="Repositório (owner/nome)"
              style={{
                width: '100%',
                padding: '9px 12px',
                backgroundColor: C.black,
                border: `1px solid ${C.blackBorder}`,
                borderRadius: 6,
                color: C.neutral,
                fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <select
              id="repo-select"
              value={repo}
              onChange={handleRepoSelect}
              disabled={loadingRepos}
              style={{
                width: '100%',
                padding: '9px 12px',
                backgroundColor: C.black,
                border: `1px solid ${C.blackBorder}`,
                borderRadius: 6,
                color: repo ? C.neutral : C.neutralMuted,
                fontSize: 13,
              }}
            >
              <option value="">
                {loadingRepos ? 'Carregando repositórios...' : 'Selecionar repositório...'}
              </option>
              {ghRepos.map((r) => (
                <option key={r.full_name} value={r.full_name}>
                  {r.private ? '🔒 ' : ''}{r.full_name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div style={{ width: 160 }}>
          <label
            htmlFor="branch-select"
            style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}
          >
            Branch{' '}
            {loadingBranches && <span style={{ color: C.neutralMuted }}>· …</span>}
            {!loadingBranches && realBranches.length > 0 && (
              <span style={{ color: C.cyber }}>· {realBranches.length}</span>
            )}
          </label>
          <select
            id="branch-select"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            disabled={loadingBranches}
            style={{
              width: '100%',
              padding: '9px 12px',
              backgroundColor: C.black,
              border: `1px solid ${C.blackBorder}`,
              borderRadius: 6,
              color: C.neutral,
              fontSize: 13,
            }}
          >
            {branchOptions.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
        <Btn
          type="submit"
          variant="outline"
          size="sm"
          icon={Plus}
          disabled={!repo || addRepo.isPending}
          aria-busy={addRepo.isPending}
        >
          {addRepo.isPending ? 'Adicionando...' : 'Adicionar'}
        </Btn>
      </div>

      {providerConnected && ghReposQuery.error && (
        <div style={{ fontSize: 11.5, color: C.neutralMuted, marginTop: 8 }}>
          Não foi possível listar do GitHub ({ghReposQuery.error.status ?? 'erro'}).
          Digite o repositório no formato owner/nome.
        </div>
      )}
    </form>
  );
}

// ─── StepGit ──────────────────────────────────────────────────────────────────

// 3ª opção de importação: indexar um repositório GitHub PÚBLICO por URL, sem
// conectar conta. Clona sem token e indexa na memória (RAG) do projeto.
function PublicUrlIndex({ projectId }) {
  const [url, setUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [repoId, setRepoId] = useState(null);
  const [repoName, setRepoName] = useState('');
  const publicIndex = usePublicIndex(projectId);
  const statusQuery = useIndexingStatus(projectId, repoId, !!repoId);
  const status = statusQuery.data;

  const submit = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u || publicIndex.isPending) return;
    try {
      const data = await publicIndex.mutateAsync({ url: u, branch: branch.trim() || 'main' });
      setRepoId(data?.repo_id || null);
      setRepoName(data?.full_name || u);
    } catch { /* erro exibido via MutationError */ }
  };

  const pct = Math.round(status?.progress_pct ?? 0);
  const done = status?.status === 'done';
  const failed = status?.status === 'failed';
  // Enquanto o repo é indexado passamos por fases SEM % real (clone/leitura/grafo);
  // só o embed produz progresso contável. Rotulamos a fase e usamos barra animada
  // (indeterminada) até haver % — assim fica claro que está trabalhando.
  const STEP_LABEL = {
    initializing: 'Preparando…',
    clone_repo: 'Clonando o repositório…',
    chunk_files: 'Lendo e fatiando os arquivos…',
    build_repo_graph: 'Construindo o grafo de código…',
    embed_and_upsert: 'Gerando embeddings e indexando…',
    update_progress: 'Finalizando…',
    done: 'Indexado ✓',
    failed: 'Falha na indexação',
    cancelled: 'Cancelado',
  };
  const running = !!repoId && !done && !failed;
  const phaseLabel = failed
    ? 'Falha na indexação'
    : done
      ? 'Indexado ✓'
      : (STEP_LABEL[status?.current_step] || 'Iniciando indexação…');
  const indeterminate = running && pct <= 0; // sem % real ainda → barra animada
  const inputStyle = {
    width: '100%', padding: '9px 12px', backgroundColor: C.black,
    border: `1px solid ${C.blackBorder}`, borderRadius: 6, color: C.neutral, fontSize: 13,
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: C.neutral, marginBottom: 4 }}>
        Ou: indexar um repositório GitHub público por URL
      </div>
      <div style={{ fontSize: 12.5, color: C.neutralDim, marginBottom: 12 }}>
        Cola a URL de um repositório público — indexamos direto na memória do projeto,
        sem precisar conectar conta.
      </div>
      <MutationError error={publicIndex.error} onDismiss={() => publicIndex.reset()} />
      <form onSubmit={submit} className="flex items-end gap-3 flex-wrap">
        <div style={{ flex: '1 1 320px' }}>
          <label htmlFor="pub-url" style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
            URL do repositório
          </label>
          <input id="pub-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://github.com/owner/repo" style={inputStyle} />
        </div>
        <div style={{ flex: '0 0 140px' }}>
          <label htmlFor="pub-branch" style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
            Branch
          </label>
          <input id="pub-branch" value={branch} onChange={(e) => setBranch(e.target.value)}
            placeholder="main" style={inputStyle} />
        </div>
        <Btn type="submit" disabled={!url.trim() || publicIndex.isPending} aria-busy={publicIndex.isPending}>
          {publicIndex.isPending ? 'Iniciando...' : 'Indexar URL'}
        </Btn>
      </form>
      {repoId && (
        <div style={{ marginTop: 14 }} role="status" aria-live="polite">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: C.neutralDim, marginBottom: 6 }}>
            {running && (
              <span
                aria-hidden="true"
                style={{
                  width: 12, height: 12, flexShrink: 0, borderRadius: '50%',
                  border: `2px solid ${C.cyber}`, borderTopColor: 'transparent',
                  display: 'inline-block', animation: 'navaSpin 0.8s linear infinite',
                }}
              />
            )}
            <span style={{ fontWeight: 600, color: failed ? C.bad : done ? C.cyber : C.neutral }}>
              {phaseLabel}
            </span>
            <span style={{ color: C.neutralDim }}>· {repoName}</span>
            {!indeterminate && !failed && !done && <span>· {pct}%</span>}
            {status?.indexed_docs != null && status.total_docs
              ? <span>· {status.indexed_docs}/{status.total_docs} arquivos</span>
              : null}
          </div>
          <div style={{ position: 'relative', height: 8, background: C.black, borderRadius: 4, overflow: 'hidden', border: `1px solid ${C.blackBorder}` }}>
            {indeterminate ? (
              <div
                style={{
                  position: 'absolute', top: 0, bottom: 0, borderRadius: 4,
                  background: C.cyber, animation: 'navaIndeterminate 1.15s ease-in-out infinite',
                }}
              />
            ) : (
              <div style={{ width: `${done ? 100 : pct}%`, height: '100%', background: failed ? C.bad : C.cyber, borderRadius: 4, transition: 'width 0.4s ease' }} />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export function StepGit({ onNext, onBack }) {
  const { projectId } = useJourney();

  const providersQuery = useGitProviders();
  const reposQuery     = useGitRepositories(projectId);
  const removeRepo     = useRemoveGitRepository(projectId);

  // Aviso inline quando o usuário clica "Conectar" num provider sem OAuth app.
  // NÃO navegamos para fora da SPA (isso derrubava o wizard para o Dashboard).
  const [connectNotice, setConnectNotice] = useState('');

  const repos     = reposQuery.data ?? [];
  // Estado do grafo de código por repositório (status.json gravado pelo worker).
  const grafos    = useGraphStatus(projectId).data?.repos ?? {};
  const providers = providersQuery.data ?? [];

  function isConnected(providerName) {
    return providers.some(
      (p) => p.provider === providerName && p.status === 'connected',
    );
  }

  function getUsername(providerName) {
    return providers.find((p) => p.provider === providerName)?.username;
  }

  function getAuthMethod(providerName) {
    return providers.find((p) => p.provider === providerName)?.auth_method;
  }

  const handleConnect = useCallback((providerName) => {
    // Sem OAuth app (GITHUB_CLIENT_ID/SECRET) neste ambiente. O GitHub já aparece
    // conectado quando há GITHUB_WRITE_TOKEN. Para os demais, orientamos a
    // adicionar repositórios por URL — mantendo o usuário na tela.
    const nome = providerName === 'github' ? 'GitHub' : 'Bitbucket';
    setConnectNotice(
      `${nome}: OAuth interativo não está configurado neste ambiente ` +
      `(sem GITHUB_CLIENT_ID). O GitHub conecta automaticamente quando ` +
      `GITHUB_WRITE_TOKEN está definido. Você pode adicionar repositórios ` +
      `por URL (owner/nome) no formulário abaixo.`,
    );
  }, []);

  async function handleRemove(repoId) {
    try {
      await removeRepo.mutateAsync(repoId);
    } catch { /* tratado pelo MutationError acima */ }
  }

  function handleContinue() {
    // Pode continuar mesmo sem indexar (indexação é recomendada, não bloqueante no wizard)
    onNext?.();
  }

  const canContinue = repos.length > 0;

  return (
    <div>
      {/* Título */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Repositório Git
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          Conecte seu provedor Git e selecione os repositórios do projeto.
          Cada repositório é clonado e indexado no Repo Knowledge Pack.
        </p>
      </div>

      {/* Erro de remoção */}
      {removeRepo.error && (
        <MutationError error={removeRepo.error} onDismiss={() => removeRepo.reset()} />
      )}

      {/* Providers */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.neutralDim, marginBottom: 12, letterSpacing: '0.04em' }}>
          PROVEDORES GIT
        </div>

        {providersQuery.isLoading && <LoadingSpinner label="Carregando providers..." size={20} />}
        {providersQuery.error && (
          <QueryError error={providersQuery.error} onRetry={() => providersQuery.refetch()} />
        )}

        {!providersQuery.isLoading && (
          <div className="flex flex-col gap-3">
            <ProviderCard
              provider="github"
              connected={isConnected('github')}
              username={getUsername('github')}
              authMethod={getAuthMethod('github')}
              onConnect={handleConnect}
            />
            <ProviderCard
              provider="bitbucket"
              connected={isConnected('bitbucket')}
              username={getUsername('bitbucket')}
              authMethod={getAuthMethod('bitbucket')}
              onConnect={handleConnect}
            />
          </div>
        )}

        {connectNotice && (
          <div
            role="status"
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 6,
              backgroundColor: `${C.cyber}0d`,
              border: `1px solid ${C.cyberDim}`,
              fontSize: 12.5,
              color: C.neutralDim,
              lineHeight: 1.5,
            }}
          >
            {connectNotice}
          </div>
        )}
      </div>

      {/* 3ª opção: repositório GitHub público por URL (sem conta conectada) */}
      <PublicUrlIndex projectId={projectId} />

      {/* Repositórios cadastrados */}
      <Card style={{ marginBottom: 24 }}>
        <div className="flex items-center justify-between mb-4">
          <div style={{ fontSize: 14, fontWeight: 600, color: C.neutral }}>
            Repositórios do projeto
          </div>
          {reposQuery.isLoading && <LoadingSpinner label="Carregando..." size={16} />}
        </div>

        {reposQuery.error && (
          <QueryError error={reposQuery.error} onRetry={() => reposQuery.refetch()} />
        )}

        {/* Lista de repos */}
        {repos.length === 0 && !reposQuery.isLoading && (
          <div style={{ fontSize: 13, color: C.neutralMuted, padding: '8px 0' }}>
            Nenhum repositório adicionado. Adicione ao menos um para continuar.
          </div>
        )}

        {repos.map((repo) => (
          <div
            key={repo.id}
            style={{
              padding: '12px 0',
              borderBottom: `1px solid ${C.blackBorder}`,
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Github size={13} style={{ color: C.neutralDim }} aria-hidden="true" />
                  <span style={{ fontSize: 14, color: C.neutral, fontWeight: 500 }}>
                    {repo.full_name ?? repo.name ?? repo.id}
                  </span>
                  <Badge color={C.cyberDim} light>{repo.branch ?? 'main'}</Badge>
                </div>
                {/* Estado PERSISTIDO da indexação (2026-09-23): a linha lia `status` e
                    `files_count`, que a API não devolve — nunca aparecia nada. */}
                <div style={{ fontSize: 12, color: C.neutralMuted }}>
                  {repo.knowledge_pack_status === 'ready'
                    && `Indexado · ${(repo.indexed_docs ?? 0).toLocaleString('pt-BR')} trecho(s)`}
                  {['pending', 'cloning', 'indexing'].includes(repo.knowledge_pack_status)
                    && 'Aguardando indexação'}
                  {repo.knowledge_pack_status === 'failed' && 'Falha na última indexação — indexe de novo'}
                  {/* Índice de branch diferente da cadastrada: a execução brownfield é
                      recusada até reindexar ("(default)" é o registro antigo, anterior
                      à resolução da default pelo nome). */}
                  {repo.indexed_branch && repo.indexed_branch !== (repo.branch ?? 'main') && (
                    <span style={{ color: C.hot, marginLeft: 6 }}>
                      · o índice é da branch {repo.indexed_branch === '(default)' ? 'padrão' : repo.indexed_branch},
                      {' '}não da cadastrada ({repo.branch ?? 'main'}) — indexe de novo
                    </span>
                  )}
                </div>
                <GrafoDoRepo grafo={grafos[repo.id]} />
              </div>
              <div className="flex items-center gap-2">
                {projectId && repo.id && (
                  <RepoRoleSelect projectId={projectId} repoId={repo.id} role={repo.role} />
                )}
                <button
                  onClick={() => handleRemove(repo.id)}
                  aria-label={`Remover repositório ${repo.full_name ?? repo.id}`}
                  title="Remover"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: C.neutralMuted,
                    padding: 4,
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.hot)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = C.neutralMuted)}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Indexação individual */}
            {projectId && repo.id && (
              <IndexingProgressBar
                projectId={projectId}
                repoId={repo.id}
                repoName={repo.full_name ?? repo.name ?? repo.id}
              />
            )}
          </div>
        ))}

        {/* Adicionar repo */}
        {projectId && (
          <AddRepoForm
            projectId={projectId}
            connectedProviders={['github', 'bitbucket'].filter((p) => isConnected(p))}
            onAdded={() => reposQuery.refetch()}
          />
        )}

        {!projectId && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 6,
              backgroundColor: `${C.hot}11`,
              border: `1px solid ${C.hot}44`,
              fontSize: 13,
              color: C.neutralDim,
            }}
            role="note"
          >
            Salve a etapa anterior (Tipo do Projeto) para poder adicionar repositórios.
          </div>
        )}
      </Card>

      {/* Nota informativa */}
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 6,
          backgroundColor: `${C.cyber}08`,
          border: `1px solid ${C.blackBorder}`,
          fontSize: 12,
          color: C.neutralDim,
          marginBottom: 28,
          lineHeight: 1.5,
        }}
        role="note"
      >
        Suporta mono ou multi-repo. A indexação constrói o{' '}
        <span style={{ color: C.cyber }}>"Repo Knowledge Pack"</span> — conjunto de
        embeddings que guia os agentes a respeitar os padrões do projeto.
      </div>

      {/* Navegação */}
      <div className="flex items-center justify-between">
        <Btn variant="ghost" size="md" onClick={onBack}>
          Voltar
        </Btn>
        <Btn
          variant="primary"
          size="lg"
          onClick={handleContinue}
          disabled={!canContinue}
          aria-disabled={!canContinue}
        >
          Continuar — Importação
        </Btn>
      </div>
    </div>
  );
}
